import { useCallback, useEffect, useState } from 'react';

import apisConfig from '@/config/apis.config';
import fetchWrapper from '@/services/fetchWrapper.service';

const API_BASE = `${apisConfig.apis.api.url}/art-games/color-mix`;
const LEADERBOARD_UPDATED_EVENT = 'color-mix:leaderboard-updated';

export const LEADERBOARD_LIMIT = 100;

export const ROUND_THRESHOLDS = [
  { min: 1, max: 9, threshold: 90 },
  { min: 10, max: 14, threshold: 93 },
  { min: 15, max: 24, threshold: 95 },
  { min: 25, max: Infinity, threshold: 97 },
];

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const resolveLeaderboardUsername = (entry) => entry?.displayName || 'Anonymous';

export const getRoundBand = (round) => {
  const normalized = Number.isFinite(round) ? Math.max(1, Math.floor(round)) : 1;
  return ROUND_THRESHOLDS.find((band) => normalized >= band.min && normalized <= band.max)
    ?? ROUND_THRESHOLDS[ROUND_THRESHOLDS.length - 1];
};

export const getRoundThreshold = (round) => getRoundBand(round).threshold;

export const calculateRoundPoints = (match, threshold) => {
  const clampedMatch = clamp(match, 0, 100);
  if (clampedMatch < threshold) {
    return 0;
  }

  if (threshold >= 100) {
    return 20;
  }

  const basePoints = 20;
  const span = 100 - threshold;
  const raw = span <= 0 ? 0 : (24 * (clampedMatch - threshold)) / span;
  const bonus = clamp(Math.floor(raw), 0, 24);

  return basePoints + bonus;
};

export const evaluateRound = ({ match, poursUsed, round, streak, totalPoints, totalPours }) => {
  const resolvedRound = Number.isInteger(round) && round >= 1 ? round : (Number.isFinite(streak) ? Math.max(0, Math.floor(streak)) : 0) + 1;
  const band = getRoundBand(resolvedRound);
  const threshold = band.threshold;
  const clampedMatch = clamp(match, 0, 100);
  const isCorrect = clampedMatch >= threshold;
  const roundPoints = isCorrect ? calculateRoundPoints(clampedMatch, threshold) : 0;
  const poursInteger = Math.max(0, Math.round(poursUsed));

  const nextStreak = isCorrect ? resolvedRound : 0;
  const updatedTotalPoints = totalPoints + roundPoints;
  const updatedTotalPours = totalPours + poursInteger;

  return {
    band,
    threshold,
    clampedMatch,
    isCorrect,
    roundPoints,
    nextStreak,
    updatedTotalPoints,
    updatedTotalPours,
    poursInteger,
  };
};

const normalizeLeaderboardEntry = (entry) => {
  const currentStreak = Number.isFinite(entry?.streak) ? entry.streak : 0;
  const bestStreakCandidate = Number.isFinite(entry?.bestStreak)
    ? entry.bestStreak
    : Number.isFinite(entry?.best)
      ? entry.best
      : undefined;
  const bestStreak = Number.isFinite(bestStreakCandidate)
    ? bestStreakCandidate
    : currentStreak;

  return {
    id: entry?.userId ?? entry?.id ?? entry?.uid ?? null,
    displayName: resolveLeaderboardUsername(entry),
    bestStreak,
    currentStreak,
    totalPoints: entry?.totalPoints ?? entry?.points ?? 0,
    totalPours: entry?.totalPours ?? entry?.pours ?? 0,
    roundsPlayed: entry?.roundsPlayed ?? entry?.rounds ?? 0,
    rank: entry?.rank ?? entry?.displayRank ?? null,
  };
};

export const parseLeaderboardResponse = (data) => {
  if (!data) {
    return { top: [], user: null };
  }

  const topEntries = Array.isArray(data.top)
    ? data.top
    : Array.isArray(data.entries)
      ? data.entries
      : Array.isArray(data)
        ? data
        : [];

  const top = topEntries.map(normalizeLeaderboardEntry).slice(0, LEADERBOARD_LIMIT);
  const user = data.user ? normalizeLeaderboardEntry(data.user) : null;

  return {
    top,
    user,
  };
};

export const createRoundPayload = ({ match, poursUsed, round, roundId, userUsername }) => {
  return {
    displayName: userUsername || 'Anonymous',
    roundId,
    round,
    match: clamp(match, 0, 100),
    pours: Math.round(poursUsed),
  };
};

export const submitRoundResult = async (payload, options = {}) => {
  if (!payload) {
    return null;
  }

  const {
    emitUpdateEvent = true,
  } = options;

  const { response, data } = await fetchWrapper.post(`${API_BASE}/rounds`, {
    injectToken: true,
    body: payload,
  });

  if (!response.ok) {
    const message = data?.message || 'Unable to save Color Mix round.';
    throw new Error(message);
  }

  if (emitUpdateEvent && typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(LEADERBOARD_UPDATED_EVENT));
  }

  return data;
};

export const fetchLeaderboard = async () => {
  const { response, data } = await fetchWrapper.get(`${API_BASE}/leaderboard?limit=${LEADERBOARD_LIMIT}`, {
    injectToken: true,
  });

  if (!response.ok) {
    const message = data?.message || 'Unable to fetch Color Mix leaderboard.';
    throw new Error(message);
  }

  return parseLeaderboardResponse(data);
};

export const useColorMixLeaderboard = () => {
  const [entries, setEntries] = useState([]);
  const [userRow, setUserRow] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadLeaderboard = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await fetchLeaderboard();
      setEntries(result.top);
      setUserRow(result.user);
      setError(null);
    } catch (err) {
      setError(err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadLeaderboard();
  }, [loadLeaderboard]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const handler = () => {
      loadLeaderboard();
    };

    window.addEventListener(LEADERBOARD_UPDATED_EVENT, handler);
    return () => window.removeEventListener(LEADERBOARD_UPDATED_EVENT, handler);
  }, [loadLeaderboard]);

  return {
    entries,
    isLoading,
    error,
    userRow,
    refresh: loadLeaderboard,
  };
};

export default useColorMixLeaderboard;
